import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="terms-page">
      {/* Header */}
      <header className="bg-white px-4 py-5 border-b border-gray-200">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost"
            onClick={() => navigate('/')}
            data-testid="back-button"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-strong)' }}>Términos y Condiciones de Uso – Providers</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12">
          <div className="prose prose-slate max-w-none">
            <p className="text-sm text-gray-500 mb-6">
              <strong>Última actualización:</strong> 19 de febrero de 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                1. Naturaleza del Servicio
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Providers es un emprendimiento independiente en etapa piloto, sin fines de lucro, que actúa exclusivamente como guía telefónica digital y facilitador de contacto entre personas que solicitan servicios y personas que ofrecen servicios.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Providers no presta servicios técnicos, profesionales, comerciales ni de ningún otro tipo.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Providers no intermedia pagos, no percibe comisiones, no retiene fondos ni participa en la negociación, ejecución o cumplimiento de los servicios contratados entre las partes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                2. Independencia de las Partes
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los Usuarios (personas que solicitan servicios) y los Proveedores (personas que ofrecen servicios) reconocen y aceptan que:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Providers no es parte del contrato que eventualmente celebren entre ellos.</li>
                <li>La relación contractual se establece exclusivamente entre Usuario y Proveedor.</li>
                <li>No existe relación laboral, societaria, de agencia, representación, mandato ni dependencia entre Providers y los Proveedores.</li>
                <li>Providers no ejerce control, supervisión ni dirección sobre la actividad de los Proveedores.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Cada Proveedor actúa de manera autónoma e independiente, siendo el único responsable por su actividad económica, obligaciones fiscales, previsionales, laborales y de seguridad social.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                3. Exclusión de Responsabilidad
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Providers no será responsable por:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>La calidad, idoneidad o resultado de los servicios contratados.</li>
                <li>Incumplimientos contractuales entre las partes.</li>
                <li>Daños materiales o personales.</li>
                <li>Accidentes.</li>
                <li>Robos.</li>
                <li>Pérdidas económicas.</li>
                <li>Disputas de pago.</li>
                <li>Métodos de cobro utilizados.</li>
                <li>Actos ilícitos de cualquier naturaleza.</li>
                <li>Cualquier incidente ocurrido durante o con motivo del servicio.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Los Usuarios y Proveedores asumen plena responsabilidad por las decisiones que adopten al contratar entre sí.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                4. Veracidad de la Información
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cada Usuario y Proveedor declara bajo su exclusiva responsabilidad que la información proporcionada es veraz, actual y completa.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                El Proveedor declara bajo su exclusiva responsabilidad que toda la información y documentación aportada a Providers es auténtica, vigente, completa y veraz.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                El Proveedor reconoce que la presentación de documentación falsa, adulterada, incompleta o correspondiente a terceros podrá implicar su exclusión inmediata del programa piloto.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Providers no realiza durante esta etapa piloto procesos de auditoría documental, verificación pericial ni validación de autenticidad sobre la documentación aportada por los Proveedores, limitándose exclusivamente a recibir y almacenar la información declarada por éstos.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Providers no verifica antecedentes penales, técnicos, profesionales ni comerciales de los Proveedores.
              </p>
              <p className="text-gray-700 leading-relaxed">
                La inclusión de un Proveedor en el listado o plataforma no implica validación, certificación, aprobación, habilitación profesional ni garantía alguna por parte de Providers respecto de la identidad, antecedentes, experiencia, idoneidad o conducta del Proveedor.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                5. Carácter Experimental del Proyecto
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Las partes reconocen que Providers se encuentra en etapa piloto o experimental.
              </p>
              <p className="text-gray-700 leading-relaxed">
                El servicio puede ser modificado, suspendido o discontinuado en cualquier momento sin previo aviso.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                6. Ausencia de Relación Laboral
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los Proveedores reconocen expresamente que:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>No existe vínculo laboral con Providers.</li>
                <li>No perciben remuneración de Providers.</li>
                <li>No reciben instrucciones, horarios ni exclusividad.</li>
                <li>No existe subordinación técnica, jurídica ni económica.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Cada Proveedor actúa por cuenta propia y bajo su exclusivo riesgo.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                7. Exoneración y Manifiesto de Indemnidad
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Usuarios y Proveedores se obligan a mantener indemne a Providers frente a cualquier reclamo judicial o extrajudicial derivado de la relación contractual que celebren entre sí.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                8. Jurisdicción
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Para cualquier controversia vinculada exclusivamente con el uso de la plataforma de contacto, las partes se someten a la jurisdicción de los tribunales ordinarios de la Ciudad Autónoma de Buenos Aires, renunciando a cualquier otro fuero o jurisdicción que pudiera corresponder.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
                9. Aceptación
              </h2>
              <p className="text-gray-700 leading-relaxed">
                El uso del servicio implica la aceptación plena y sin reservas de los presentes Términos y Condiciones.
              </p>
            </section>
          </div>

          {/* Botón volver */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <Button
              data-testid="back-to-home-btn"
              onClick={() => navigate('/')}
              style={{ backgroundColor: 'var(--primary-strong)' }}
              className="px-8"
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
